import { z } from 'zod'

// Position schema for React Flow
export const positionSchema = z.object({
  x: z.number().default(0),
  y: z.number().default(0),
})

// Condition operand (D-05)
export const conditionOperandSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('variable'), ref: z.string().min(1) }),
  z.object({ kind: z.literal('literal'), value: z.string() }),
])

// Condition expression (D-05)
export const conditionOperatorSchema = z.enum([
  'equals',
  'not_equals',
  'contains',
  'gt',
  'lt',
  'is_empty',
  'is_not_empty',
])

export const conditionSchema = z.object({
  operator: conditionOperatorSchema,
  left: conditionOperandSchema,
  right: conditionOperandSchema.optional(), // optional for is_empty/is_not_empty
})

export type Condition = z.infer<typeof conditionSchema>

// Error handling (D-09)
export const errorHandlingSchema = z.object({
  retry_count: z.number().int().min(0).max(5).default(0),
  retry_backoff: z.enum(['linear', 'exponential']).default('linear'),
  fallback_node_id: z.string().nullable().default(null),
  on_error: z
    .enum(['fail_workflow', 'continue', 'use_default_output'])
    .default('fail_workflow'),
  default_output: z.string().nullable().default(null), // JSON string, shown when on_error = use_default_output
})

// Schema field for node I/O (reuse pattern from Phase 1)
export const nodeIOFieldSchema = z.object({
  name: z.string().min(1),
  type: z.enum(['string', 'number', 'boolean', 'array', 'object']),
})

// Variable mapping per node
export const nodeVariableMappingSchema = z.object({
  source_node_id: z.string().min(1),
  source_output: z.string().min(1),
  target_input: z.string().min(1),
})

// --- Type-specific config schemas (D-07) ---

export const llmConfigSchema = z.object({
  system_prompt: z.string().default(''),
  user_prompt_template: z.string().default(''),
  model: z.string().min(1).default('gpt-4'),
  temperature: z.number().min(0).max(2).default(0.7),
  max_tokens: z.number().int().positive().max(128000).default(1024),
})

export const codeConfigSchema = z.object({
  language: z.literal('python'),
  source: z.string().default(''),
  timeout_seconds: z.number().int().positive().max(300).default(30),
})

export const httpConfigSchema = z.object({
  method: z
    .enum(['GET', 'POST', 'PUT', 'PATCH', 'DELETE'])
    .default('GET'),
  url: z.string().default(''),
  headers: z
    .array(z.object({ key: z.string(), value: z.string() }))
    .default([]),
  body: z.string().optional(),
  auth_type: z.enum(['none', 'bearer', 'basic']).default('none'),
  auth_value: z.string().optional(),
})

export const agentConfigSchema = z.object({
  agent_id: z.string().default(''),
  input_overrides: z
    .array(z.object({ key: z.string(), value: z.string() }))
    .default([]),
})

// --- Phase 4 plan 04-04: inheritance-wrapped LLM config (D-09) ---
//
// Wraps each inheritable field in `{inherited, value}`. When `inherited=true`
// the workflow-level default applies at resolution time; when false the
// node value wins. Mirror of backend `workflow_llm_resolver.py`.

export const inheritableFieldSchema = <T extends z.ZodTypeAny>(valueSchema: T) =>
  z.object({
    inherited: z.boolean(),
    value: valueSchema.nullable(),
  })

// Gemini safety enums — match backend _GEMINI_THRESHOLD_MAP keys (UI side).
export const GEMINI_THRESHOLDS = [
  'off',
  'block_none',
  'block_only_high',
  'block_medium_and_above',
  'block_low_and_above',
] as const
export const GEMINI_CATEGORIES = [
  'harassment',
  'hate_speech',
  'sexually_explicit',
  'dangerous',
] as const

export const llmNodeConfigSchema = z.object({
  model: inheritableFieldSchema(z.string()),
  temperature: inheritableFieldSchema(z.number().min(0).max(2)),
  max_tokens: inheritableFieldSchema(z.number().int().positive()),
  api_key_id: z.string().uuid().nullable().optional(),
  advanced: z
    .object({
      top_p: inheritableFieldSchema(z.number().min(0).max(1)).optional(),
      frequency_penalty: inheritableFieldSchema(
        z.number().min(-2).max(2)
      ).optional(),
      presence_penalty: inheritableFieldSchema(
        z.number().min(-2).max(2)
      ).optional(),
      stop: inheritableFieldSchema(z.array(z.string()).max(4)).optional(),
      seed: inheritableFieldSchema(z.number().int()).optional(),
    })
    .default({}),
  model_specific: z
    .object({
      thinking: z
        .object({
          enabled: z.boolean(),
          budget_tokens: z.number().int().positive().optional(),
        })
        .optional(),
      reasoning_effort: z
        .enum(['minimal', 'low', 'medium', 'high', 'none'])
        .optional(),
      safety_settings: z
        .record(z.enum(GEMINI_CATEGORIES), z.enum(GEMINI_THRESHOLDS))
        .optional(),
    })
    .default({}),
})

export type LLMNodeConfig = z.infer<typeof llmNodeConfigSchema>

/**
 * Migrate a flat Phase-2 LLM node config into the wrapped Phase-4 blob.
 *
 * RESEARCH P-08: existing concrete values are treated as overrides
 * (`inherited=false`) so re-saving a pre-Phase-4 workflow never silently
 * adopts a different workflow default.
 *
 * Idempotent: if called on an already-wrapped config the result is equivalent.
 */
export function migrateNodeConfigToInheritanceBlob(
  flat: Record<string, unknown>
): LLMNodeConfig {
  const wrap = <T>(
    value: T | undefined
  ): { inherited: boolean; value: T | null } =>
    value === undefined
      ? { inherited: true, value: null }
      : { inherited: false, value }
  return {
    model: wrap(flat.model as string | undefined),
    temperature: wrap(flat.temperature as number | undefined),
    max_tokens: wrap(flat.max_tokens as number | undefined),
    api_key_id: (flat.api_key_id as string | null | undefined) ?? null,
    advanced: (flat.advanced as LLMNodeConfig['advanced']) ?? {},
    model_specific:
      (flat.model_specific as LLMNodeConfig['model_specific']) ?? {},
  }
}

/**
 * Type guard: true iff `cfg` already carries the wrapped Phase-4 shape.
 */
export function isWrappedNodeConfig(cfg: unknown): cfg is LLMNodeConfig {
  if (typeof cfg !== 'object' || cfg === null) return false
  const maybe = cfg as { model?: unknown }
  if (typeof maybe.model !== 'object' || maybe.model === null) return false
  const modelEntry = maybe.model as { inherited?: unknown }
  return modelEntry.inherited !== undefined
}

// --- Node types as string literal ---
export const nodeTypeSchema = z.enum(['llm', 'code', 'http', 'agent'])
export type NodeType = z.infer<typeof nodeTypeSchema>

// --- Shared node fields ---
const baseNodeFields = {
  id: z.string(),
  label: z.string().default(''),
  position: positionSchema,
  input_schema: z.array(nodeIOFieldSchema).default([]),
  output_schema: z.array(nodeIOFieldSchema).default([]),
  variable_mappings: z.array(nodeVariableMappingSchema).default([]),
  error_handling: errorHandlingSchema.default({}),
}

// --- Discriminated union for node config (D-12) ---
export const workflowNodeSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('llm'),
    config: llmConfigSchema.default({}),
    ...baseNodeFields,
  }),
  z.object({
    type: z.literal('code'),
    config: codeConfigSchema.default({ language: 'python' }),
    ...baseNodeFields,
  }),
  z.object({
    type: z.literal('http'),
    config: httpConfigSchema.default({}),
    ...baseNodeFields,
  }),
  z.object({
    type: z.literal('agent'),
    config: agentConfigSchema.default({}),
    ...baseNodeFields,
  }),
])

export type FlowNode = z.infer<typeof workflowNodeSchema>

// --- Edge schema (D-12) ---
export const workflowEdgeSchema = z.object({
  id: z.string(),
  source: z.string(),
  target: z.string(),
  condition: conditionSchema.nullable().default(null),
  branch_label: z.string().nullable().default(null),
})

export type FlowEdge = z.infer<typeof workflowEdgeSchema>

// --- Default configs per type ---
export const defaultConfigByType: Record<NodeType, unknown> = {
  llm: llmConfigSchema.parse({}),
  code: codeConfigSchema.parse({ language: 'python' }),
  http: httpConfigSchema.parse({}),
  agent: agentConfigSchema.parse({}),
}
